/**
 * DailyServingModal - Multi-step wizard to change daily serving
 * Step 1: Select daily grams (50-2000g)
 * Step 2: Select meals for new serving
 * Step 3: Confirm price and update
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Alert,
  Slider,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScaleIcon from "@mui/icons-material/Scale";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { QuizButton } from "./shared/QuizButton";
import type { RechargeSubscription, ShopifyProduct, ShopifyVariant } from "../types";
import {
  getPackPricingConfig,
  toDailyKey50,
  type PlanDays,
} from "@/helpers/plansToPacks";
import {
  buildMealSelectionsWithBaseline,
  type MealSelectionWithBaseline,
} from "@/helpers/mealDistribution";
import { buildVariantIndex } from "../utils/recharge";
import { QUIZ } from "@/theme/quizTokens";

type DailyServingModalProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
  subscription: RechargeSubscription | null;
  products: ShopifyProduct[];
  onConfirm: (
    newDailyGrams: number,
    newPackSizeGrams: number,
    newVariantId: string,
    meals: MealSelection[],
    newPrice: number,
    mealsSummary: string
  ) => Promise<void>;
};

// Generate human-readable meals summary like "2× Salmon 800g, 3× Beef 800g"
function buildMealsSummary(meals: MealSelection[], packSize: number): string {
  return meals
    .filter((m) => m.count > 0)
    .map((m) => `${m.count}× ${m.productTitle} ${packSize}g`)
    .join(", ");
}

// Using MealSelectionWithBaseline from unified helper
type MealSelection = MealSelectionWithBaseline;

type Step = "serving" | "meals" | "confirm";

// Daily grams options (50 to 2000 in 50g increments)
const MIN_DAILY_GRAMS = 50;
const MAX_DAILY_GRAMS = 2000;

// Find hidden price product for a plan
function findHiddenPriceProduct(
  products: ShopifyProduct[],
  planDays: number
): ShopifyProduct | null {
  // Look for products with "hidden" in handle and matching plan days
  return (
    products.find((p) => {
      const handle = String(p.handle || "").toLowerCase();
      const title = String(p.title || "").toLowerCase();
      return (
        (handle.includes("hidden") && handle.includes(`${planDays}`)) ||
        (title.includes("hidden") && title.includes(`${planDays} day`))
      );
    }) || null
  );
}

// Get price for a pack size from hidden price product
function getPackPrice(hiddenProduct: ShopifyProduct | null, packSize: number): number {
  if (!hiddenProduct) return 0;

  const variant = hiddenProduct.variants?.find((v) => {
    const sizeFromTitle = parseInt(String(v.title || ""), 10);
    return sizeFromTitle === packSize;
  });

  return variant ? parseFloat(String(variant.price || "0")) : 0;
}

// Find meal/recipe products
function findMealProducts(products: ShopifyProduct[]): ShopifyProduct[] {
  return products.filter((p) => {
    const type = String(p.product_type || "").toLowerCase();
    const tags = String(p.tags || "").toLowerCase();
    return type === "recipe" || tags.includes("recipe") || tags.includes("meal");
  });
}

// findVariantForPackSize - helper to find variant matching a pack size
function findVariantForPackSize(
  product: ShopifyProduct,
  packSize: number
): ShopifyVariant | null {
  if (!product.variants?.length) return null;

  // Try to find variant with matching size in title (e.g., "800g" or "800")
  const matchingVariant = product.variants.find((v) => {
    const titleMatch = String(v.title || "").match(/(\d+)/);
    return titleMatch && parseInt(titleMatch[1], 10) === packSize;
  });

  // Fallback to first variant if no match
  return matchingVariant || product.variants[0];
}

// Get variant for bundle product
function findBundleVariant(
  products: ShopifyProduct[],
  planDays: number,
  packSizeGrams: number
): ShopifyVariant | null {
  const planProduct = products.find((p) => {
    const title = String(p.title || "").toLowerCase();
    return (
      title.includes(`${planDays} day plan`) ||
      title.includes(`${planDays}-day plan`) ||
      title.includes(`${planDays}day plan`)
    );
  });

  if (!planProduct?.variants?.length) return null;

  return (
    planProduct.variants.find((v) => {
      const sizeMatch = String(v.title || "").match(/(\d+)/);
      return sizeMatch && parseInt(sizeMatch[1], 10) === packSizeGrams;
    }) || null
  );
}

export function DailyServingModal({
  open,
  onClose,
  subscription,
  products,
  onConfirm,
}: DailyServingModalProps) {
  const [step, setStep] = useState<Step>("serving");
  const [selectedDailyGrams, setSelectedDailyGrams] = useState<number>(300);
  const [mealSelections, setMealSelections] = useState<MealSelection[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Build variant index for image lookup
  const variantIndex = useMemo(() => buildVariantIndex(products), [products]);

  // Extract current values from subscription
  const currentValues = useMemo(() => {
    if (!subscription?.properties) {
      return { dailyGrams: 300, packSizeGrams: 400, planDays: 7, currentMeals: [] };
    }

    const props = subscription.properties;

    // Get pack size
    const packSizeProp = props.find((p: any) => p.name === "_paws_pack_size_grams");
    const packSizeGrams = packSizeProp ? parseInt(packSizeProp.value, 10) : 400;

    // Get plan days
    const planDaysProp = props.find((p: any) => p.name === "_paws_plan_days");
    const planDays = planDaysProp ? parseInt(planDaysProp.value, 10) : 7;

    // Get daily grams from dog profile
    const dogJsonProp = props.find((p: any) => p.name === "_paws_dog_json");
    let dailyGrams = 300;
    if (dogJsonProp?.value) {
      try {
        const dogProfile = JSON.parse(dogJsonProp.value);
        dailyGrams = dogProfile.dailyGrams || dogProfile.daily_grams || 300;
      } catch {
        // Ignore parse errors
      }
    }

    // Get current meals
    const mealsProp = props.find((p: any) => p.name === "_paws_meals_json");
    let currentMeals: Array<{ variantId: string; count: number }> = [];
    if (mealsProp?.value) {
      try {
        currentMeals = JSON.parse(mealsProp.value);
      } catch {
        // Ignore parse errors
      }
    }

    // Clamp dailyGrams to valid range
    const clampedDailyGrams = Math.min(MAX_DAILY_GRAMS, Math.max(MIN_DAILY_GRAMS, dailyGrams));

    return {
      dailyGrams: clampedDailyGrams,
      dailyKey: toDailyKey50(clampedDailyGrams),
      packSizeGrams,
      planDays: planDays as PlanDays,
      currentMeals
    };
  }, [subscription]);

  // Calculate new pack config based on selected daily grams
  const newPackConfig = useMemo(() => {
    const { planDays } = currentValues;
    const { dailyKey, packSize, totalPacks } = getPackPricingConfig(
      selectedDailyGrams,
      planDays as PlanDays
    );

    // Get price from hidden product
    const hiddenProduct = findHiddenPriceProduct(products, planDays);
    const pricePerPack = getPackPrice(hiddenProduct, packSize);
    const totalPrice = totalPacks * pricePerPack;
    const pricePerDay = totalPrice / planDays;

    // Find bundle variant
    const bundleVariant = findBundleVariant(products, planDays, packSize);

    return {
      dailyKey,
      packSize,
      totalPacks,
      pricePerPack,
      totalPrice,
      pricePerDay,
      bundleVariantId: bundleVariant ? String(bundleVariant.id) : null,
    };
  }, [selectedDailyGrams, currentValues, products]);

  // Meal products with variant info
  const mealProducts = useMemo(() => {
    return findMealProducts(products);
  }, [products]);

  // Total meals selected
  const totalMealsSelected = useMemo(() => {
    return mealSelections.reduce((sum, m) => sum + m.count, 0);
  }, [mealSelections]);

  // Check if pack size changed
  const packSizeChanged = newPackConfig.packSize !== currentValues.packSizeGrams;

  // Track previous open state to only reset on actual modal open
  const prevOpenRef = useRef(false);

  // Reset state only when modal opens (not on data changes while open)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only reset when transitioning from closed to open
    if (open && !wasOpen) {
      setStep("serving");
      setSelectedDailyGrams(currentValues.dailyGrams);
      setError(null);
      setSuccess(false);
      setConfirming(false);
      // Clear meal selections - user will re-select based on new/current pack size
      setMealSelections([]);
    }
  }, [open, currentValues]);

  const handleNext = () => {
    if (step === "serving") {
      // Auto-distribute meals evenly across all recipes using unified helper
      const distributed = buildMealSelectionsWithBaseline(
        mealProducts,
        newPackConfig.totalPacks,
        newPackConfig.packSize,
        findVariantForPackSize
      );
      setMealSelections(distributed);
      setError(null);
      setStep("meals");
    } else if (step === "meals") {
      if (totalMealsSelected < newPackConfig.totalPacks) {
        setError(`Please select at least ${newPackConfig.totalPacks} meals`);
        return;
      }
      setError(null);
      setStep("confirm");
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === "meals") {
      setStep("serving");
    } else if (step === "confirm") {
      setStep("meals");
    }
  };

  const handleMealChange = useCallback(
    (variantId: string, delta: number, product: ShopifyProduct, variant: ShopifyVariant) => {
      setMealSelections((prev) => {
        const existing = prev.find((m) => m.variantId === variantId);

        if (existing) {
          // Allow going to 0 (no minimum per meal - total just needs to meet recommended)
          const newCount = Math.max(0, existing.count + delta);
          return prev.map((m) =>
            m.variantId === variantId ? { ...m, count: newCount } : m
          );
        }

        // Only allow adding new items (delta > 0)
        if (delta > 0) {
          return [
            ...prev,
            {
              variantId,
              count: delta,
              baseline: 0,
              productTitle: product.title,
              variantTitle: variant.title || "",
              imageUrl: product.images?.[0]?.src || (product.image as any)?.src || null,
              price: parseFloat(String(variant.price || "0")),
            },
          ];
        }

        return prev;
      });
    },
    []
  );

  const handleConfirm = async () => {
    if (!newPackConfig.bundleVariantId) {
      setError("Unable to find bundle variant. Please contact support.");
      return;
    }

    if (!newPackConfig.totalPrice || newPackConfig.totalPrice <= 0) {
      setError("Unable to calculate pricing. Please contact us at support@pawsonplates.co.uk.");
      return;
    }

    const missingTitle = mealSelections.find(m => m.count > 0 && !m.productTitle);
    if (missingTitle) {
      setError("One of your selected meals is missing information. Please contact us at support@pawsonplates.co.uk.");
      return;
    }

    setConfirming(true);
    setError(null);

    try {
      const activeMeals = mealSelections.filter(m => m.count > 0);
      const mealsSummary = buildMealsSummary(activeMeals, newPackConfig.packSize);
      const mealsWithPrice = activeMeals.map(m => ({ ...m, price: newPackConfig.pricePerPack }));
      await onConfirm(
        selectedDailyGrams,
        newPackConfig.packSize,
        newPackConfig.bundleVariantId,
        mealsWithPrice,
        newPackConfig.totalPrice,
        mealsSummary
      );
      setSuccess(true);
      setTimeout(() => {
        onClose(true);
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to update daily serving");
    } finally {
      setConfirming(false);
    }
  };

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;

  const hasChanged = selectedDailyGrams !== currentValues.dailyGrams;

  // Step indicator
  const steps = [
    { key: "serving", label: "Daily Serving" },
    { key: "meals", label: "Select Meals" },
    { key: "confirm", label: "Confirm" },
  ];
  const currentStepIndex = steps.findIndex((s) => s.key === step);

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : () => onClose(false)}
      maxWidth={step === "confirm" ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: "100%", sm: 600 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          borderBottom: "1px solid rgba(0,0,0,.08)",
          background: QUIZ.color.cream,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {step !== "serving" && (
            <IconButton
              onClick={handleBack}
              disabled={confirming}
              size="small"
              sx={{ mr: 0.5 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #FFE8D6 0%, #FFCBA4 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RestaurantIcon sx={{ fontSize: 22, color: QUIZ.color.brown }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              {steps[currentStepIndex]?.label || "Change Daily Serving"}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 11, md: 12 },
                color: QUIZ.color.brown,
                opacity: 0.65,
              }}
            >
              Step {currentStepIndex + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={() => onClose(false)}
          disabled={confirming}
          sx={{
            color: QUIZ.color.brown,
            "&:hover": { background: "rgba(0,0,0,.05)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
        {/* Success State */}
        {success && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            Daily serving updated successfully!
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Step 1: Daily Serving Selection */}
        {step === "serving" && !success && (
          <Box>
            <Typography
              sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.75, mb: 3 }}
            >
              Adjust your dog's daily serving size. This will update your pack
              size and delivery quantity.
            </Typography>

            {/* Current value display */}
            <Box
              sx={{
                textAlign: "center",
                mb: 3,
                p: 2,
                borderRadius: "12px",
                background: "#FFF8F0",
                border: "2px solid #FFD9B3",
              }}
            >
              <Typography
                sx={{
                  fontSize: 48,
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                  lineHeight: 1,
                }}
              >
                {selectedDailyGrams}g
              </Typography>
              <Typography
                sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.65, mt: 1 }}
              >
                per day
              </Typography>
            </Box>

            {/* Slider */}
            <Box sx={{ px: 2, mb: 3 }}>
              <Slider
                value={selectedDailyGrams}
                onChange={(_, value) => setSelectedDailyGrams(value as number)}
                min={MIN_DAILY_GRAMS}
                max={MAX_DAILY_GRAMS}
                step={50}
                marks={[
                  { value: 50, label: "50g" },
                  { value: 500, label: "500g" },
                  { value: 1000, label: "1000g" },
                  { value: 1500, label: "1500g" },
                  { value: 2000, label: "2000g" },
                ]}
                sx={{
                  color: QUIZ.color.brown,
                  "& .MuiSlider-thumb": {
                    width: 24,
                    height: 24,
                    backgroundColor: QUIZ.color.warmWhite,
                    border: "3px solid #4A3026",
                  },
                  "& .MuiSlider-markLabel": {
                    fontSize: 11,
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Pack info preview */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: QUIZ.color.cream,
                border: "1px solid rgba(0,0,0,.06)",
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown, mb: 1.5 }}
              >
                Your {currentValues.planDays}-Day Plan will include:
              </Typography>

              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScaleIcon sx={{ fontSize: 18, color: QUIZ.color.brown }} />
                    <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                      Pack size
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown }}>
                    {newPackConfig.packSize}g
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: QUIZ.color.brown }} />
                    <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                      Packs per delivery
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown }}>
                    {newPackConfig.totalPacks} packs
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    Total per delivery
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: QUIZ.color.brown }}>
                    {formatPrice(newPackConfig.totalPrice)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.65 }}>
                    Per day
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.65 }}>
                    {formatPrice(newPackConfig.pricePerDay)}/day
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {packSizeChanged && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
                Pack size will change from {currentValues.packSizeGrams}g to{" "}
                {newPackConfig.packSize}g. You'll need to re-select your meals.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Meal Selection */}
        {step === "meals" && !success && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.75 }}>
                Select your meals ({newPackConfig.totalPacks} required)
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                  background:
                    totalMealsSelected >= newPackConfig.totalPacks
                      ? "#E5FFEA"
                      : "#FFF3CD",
                  color:
                    totalMealsSelected >= newPackConfig.totalPacks
                      ? "#2E7D32"
                      : "#856404",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {totalMealsSelected} / {newPackConfig.totalPacks}
              </Box>
            </Box>

            <Stack spacing={1.5}>
              {mealProducts.map((product) => {
                // Find variant matching the selected pack size
                const variant = findVariantForPackSize(product, newPackConfig.packSize);
                if (!variant) return null;

                const variantId = String(variant.id);
                const selection = mealSelections.find(
                  (m) => m.variantId === variantId
                );
                const count = selection?.count || 0;
                const canDecrement = count > 0;
                const imageUrl = variantIndex.get(variantId)?.imageUrl;

                return (
                  <Box
                    key={product.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "12px",
                      border: count > 0 ? "2px solid #4A3026" : "1px solid rgba(0,0,0,.08)",
                      background: count > 0 ? "#FFF8F0" : QUIZ.color.cream,
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "8px",
                        overflow: "hidden",
                        flexShrink: 0,
                        background: QUIZ.color.cream,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={product.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <RestaurantIcon sx={{ fontSize: 24, color: "#C4A98A" }} />
                      )}
                    </Box>

                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: QUIZ.color.brown,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: QUIZ.color.brown, opacity: 0.65 }}
                      >
                        {newPackConfig.packSize}g pack
                      </Typography>
                    </Box>

                    {/* Counter */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMealChange(variantId, -1, product, variant)
                        }
                        disabled={!canDecrement}
                        sx={{
                          width: 32,
                          height: 32,
                          border: "1px solid rgba(0,0,0,.12)",
                          "&:disabled": { opacity: 0.3 },
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{
                          width: 32,
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: 16,
                        }}
                      >
                        {count}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleMealChange(variantId, 1, product, variant)
                        }
                        sx={{
                          width: 32,
                          height: 32,
                          background: QUIZ.color.brown,
                          color: QUIZ.color.white,
                          "&:hover": { background: "#5A2505" },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Step 3: Confirmation */}
        {step === "confirm" && !success && (
          <Box>
            <Typography
              sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.75, mb: 3 }}
            >
              Review your changes before confirming.
            </Typography>

            {/* Summary */}
            <Stack spacing={2}>
              {/* Daily serving */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: QUIZ.color.cream,
                  border: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}
                >
                  DAILY SERVING
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    {currentValues.dailyGrams}g → {selectedDailyGrams}g
                  </Typography>
                  {selectedDailyGrams !== currentValues.dailyGrams && (
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color:
                          selectedDailyGrams > currentValues.dailyGrams
                            ? "#2E7D32"
                            : "#D32F2F",
                      }}
                    >
                      {selectedDailyGrams > currentValues.dailyGrams ? "+" : ""}
                      {selectedDailyGrams - currentValues.dailyGrams}g
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Pack size */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: QUIZ.color.cream,
                  border: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}
                >
                  PACK SIZE
                </Typography>
                <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                  {currentValues.packSizeGrams}g → {newPackConfig.packSize}g (
                  {newPackConfig.totalPacks} packs)
                </Typography>
              </Box>

              {/* Meals */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: QUIZ.color.cream,
                  border: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.65, mb: 1 }}
                >
                  MEALS ({totalMealsSelected} packs)
                </Typography>
                <Stack spacing={0.5}>
                  {mealSelections.map((m) => (
                    <Typography
                      key={m.variantId}
                      sx={{ fontSize: 13, color: QUIZ.color.brown }}
                    >
                      {m.count}× {m.productTitle}
                    </Typography>
                  ))}
                </Stack>
              </Box>

              {/* Price */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: "#FFF8F0",
                  border: "2px solid #FFD9B3",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, mb: 1 }}
                >
                  NEW PRICE
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    Per delivery
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: QUIZ.color.brown }}>
                    {formatPrice(newPackConfig.totalPrice)}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 12, color: QUIZ.color.brown, opacity: 0.65, textAlign: "right" }}
                >
                  ({formatPrice(newPackConfig.pricePerDay)}/day)
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {!success && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <QuizButton
              variant="secondary"
              onClick={step === "serving" ? () => onClose(false) : handleBack}
              disabled={confirming}
              fullWidth
            >
              {step === "serving" ? "Cancel" : "Back"}
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={step === "confirm" ? handleConfirm : handleNext}
              disabled={
                confirming ||
                (step === "serving" && !hasChanged && !packSizeChanged) ||
                (step === "meals" && totalMealsSelected < newPackConfig.totalPacks)
              }
              fullWidth
            >
              {confirming
                ? "Updating..."
                : step === "confirm"
                ? "Confirm Changes"
                : "Next"}
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
